import React, { useMemo } from 'react';
import { Tabs, TabPanel } from 'tea-component';
import { getLocale } from 'umi';
import EventHeader from './EventHeader';
import OverviewSection from './OverviewSection';
import FactorBreakdown from './FactorBreakdown';
import RelatedLinks from './RelatedLinks';
import type { IEventDetailSidebarProps } from './types';
import { getTagNameMap } from '../utils';
import CardCommon from '@op/components/CardCommon';
import './index.scss';
import { ClassSpecial } from '@op/constants/common';
import { DBO_TM } from '@op/utils/format';

export default function EventDetailSidebar({
  visible = false,
  onClose,
  multiRegionTitle,
  eventDetails,
  activeRegion,
  setActiveRegion,
  loading,
  empty,
  hasMultipleRegions,
}: IEventDetailSidebarProps) {
  if (!visible) return null;

  const isEn = getLocale() === 'en-US';
  const locale = getLocale() as 'zh-CN' | 'en-US';
  const title = multiRegionTitle?.[locale] ?? multiRegionTitle?.['zh-CN'];

  const tabs = useMemo(
    () =>
      eventDetails.map((detail) => {
        const { regionKey } = detail;
        const { tagNameZh, tagNameEn } = getTagNameMap(regionKey);
        return {
          id: regionKey,
          label: isEn ? tagNameEn || regionKey : tagNameZh || regionKey,
        };
      }),
    [eventDetails, isEn],
  );

  // 关闭按钮操作配置
  const closeOperation = useMemo(
    () => [
      {
        symbol: 'close',
        icon: 'clonse',
        handleClick: onClose,
      },
    ],
    [onClose],
  );

  return (
    <div className={`dbo-event-detail-sidebar ${visible ? '--visible' : ''}`}>
      <CardCommon
        loadingHeight={500}
        loading={loading}
        title={hasMultipleRegions ? title : eventDetails?.[0]?.title}
        empty={empty}
        emptyHeight={500}
        operations={closeOperation}
        alwaysShowOperations
      >
        {eventDetails.length > 0 && (
          <Tabs
            tabs={tabs}
            className={hasMultipleRegions ? '' : ClassSpecial.TAB_NONE}
            activeId={activeRegion ?? undefined}
            onActive={(tab) => setActiveRegion(tab.id)}
          >
            {eventDetails.map((detail) => (
              <TabPanel key={detail.regionKey} id={detail.regionKey}>
                <EventHeader
                  trend={detail.trend}
                  title={hasMultipleRegions ? detail?.title : ''}
                  contribution={detail.contribution}
                  time={DBO_TM.timeTitle({
                    start_time: detail.startDate,
                    end_time: detail.endDate || detail.startDate,
                    date_type: 'daily',
                  })}
                />
                <div className="dbo-event-detail-sidebar__content">
                  <OverviewSection overview={detail.overview} />
                  <FactorBreakdown factors={detail.factors} />
                  <RelatedLinks links={detail.links} />
                </div>
              </TabPanel>
            ))}
          </Tabs>
        )}
      </CardCommon>
    </div>
  );
}

export type * from './types';
